import React, { useCallback, useEffect, useState } from 'react';
import {
  Alert,
  Box,
  Chip,
  CircularProgress,
  IconButton,
  Stack,
  Tooltip,
  Typography,
} from '@mui/material';
import ConfirmationNumberIcon from '@mui/icons-material/ConfirmationNumber';
import EditOutlinedIcon from '@mui/icons-material/EditOutlined';
import { useTranslation } from 'react-i18next';
import { useLocation, useNavigate, useParams } from 'react-router-dom';
import { hostApiClient } from '../../hostApiClient';
import { NotificationSnackbar } from '../../components/common/NotificationSnackbar';
import PassOfferInstancesPanel from '../../components/passes/PassOfferInstancesPanel';
import PassOfferStatusChip from '../../components/passes/PassOfferStatusChip';
import PassTargetCollection from '../../components/passes/PassTargetCollection';
import PassTargetTypeChip from '../../components/passes/PassTargetTypeChip';
import { PassOfferDetail, PassTargetDetail } from '../../models/Pass';
import {
  formatPassPrice,
  formatPassValidUntil,
  passEntitlementLabel,
} from '../../utils/passFormatting';
import { fetchPassTargets } from '../../utils/passTargets';
import { PASS_BOARDING_STUB_WIDTH, passDetailPageSx } from '../../components/passes/passDetailLayout';

type OfferLocationState = {
  flashMessage?: string;
};

const HostPassOfferDetailsPage: React.FC = () => {
  const { t, i18n } = useTranslation();
  const navigate = useNavigate();
  const location = useLocation();
  const { id } = useParams<{ id: string }>();
  const [offer, setOffer] = useState<PassOfferDetail | null>(null);
  const [targets, setTargets] = useState<PassTargetDetail[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [snackbar, setSnackbar] = useState<{ open: boolean; message: string; severity: 'success' | 'error' }>({
    open: false,
    message: '',
    severity: 'success',
  });

  useEffect(() => {
    const state = location.state as OfferLocationState | null;
    if (state?.flashMessage) {
      setSnackbar({ open: true, message: state.flashMessage, severity: 'success' });
      navigate(location.pathname, { replace: true, state: null });
    }
  }, [location.pathname, location.state, navigate]);

  const fetchOffer = useCallback(async () => {
    if (!id) return;
    try {
      setLoading(true);
      setError(null);
      const data = await hostApiClient.getPassOffer(id);
      setOffer(data);
      const resolved = await fetchPassTargets(data.targetType, data.targetIds ?? []);
      setTargets(resolved);
    } catch (err) {
      console.error(`Error fetching host pass offer ${id}:`, err);
      setError(t('hostPasses.fetchError', 'Failed to fetch pass offers'));
    } finally {
      setLoading(false);
    }
  }, [id, t]);

  useEffect(() => {
    fetchOffer();
  }, [fetchOffer]);

  if (loading) {
    return (
      <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', minHeight: '400px' }}>
        <CircularProgress />
      </Box>
    );
  }

  if (error || !offer) {
    return (
      <Box sx={{ maxWidth: 800, mx: 'auto', p: { xs: 2, sm: 4 } }}>
        <Chip
          label={`← ${t('hostPasses.backToPasses', 'Back to passes')}`}
          onClick={() => navigate('/host/passes')}
          sx={{
            mb: 2.5,
            bgcolor: 'transparent',
            color: 'text.primary',
            border: '1px solid',
            borderColor: 'divider',
            '&:hover': { bgcolor: 'transparent' },
          }}
        />
        <Alert severity="error">{error || t('hostPasses.fetchError', 'Failed to fetch pass offers')}</Alert>
      </Box>
    );
  }

  const isArchived = offer.status === 'ARCHIVED';
  const purchaseCount = offer.purchaseCount ?? 0;
  const activeCount = offer.activeInstanceCount ?? 0;
  const createdLabel = formatPassValidUntil(offer.createdAt, i18n.language);
  const updatedLabel = formatPassValidUntil(offer.updatedAt, i18n.language);
  const metaItems = [
    {
      label: t('hostPasses.validFor', 'Valid for'),
      value: t('hostPasses.validityDays', '{{count}} days', { count: offer.validityDurationDays }),
    },
    {
      label: t('hostPasses.validityStartPolicy', 'Validity starts'),
      value:
        offer.validityStartPolicy === 'FROM_FIRST_USE'
          ? t('hostPasses.validityStartPolicyFromFirstUse', 'From first use')
          : t('hostPasses.validityStartPolicyFromPurchase', 'From purchase'),
    },
    {
      label: t('hostPasses.details.created', 'Created'),
      value: createdLabel || '—',
    },
    ...(updatedLabel && updatedLabel !== createdLabel
      ? [{ label: t('hostPasses.details.updatedLabel', 'Updated'), value: updatedLabel }]
      : []),
  ];

  return (
    <Box sx={passDetailPageSx}>
      <Chip
        label={`← ${t('hostPasses.backToPasses', 'Back to passes')}`}
        onClick={() => navigate('/host/passes')}
        sx={{
          mb: 2.5,
          bgcolor: 'transparent',
          color: 'text.primary',
          border: '1px solid',
          borderColor: 'divider',
          '&:hover': { bgcolor: 'transparent' },
        }}
      />

      {isArchived ? (
        <Alert severity="info" sx={{ mb: 2 }}>
          {t('hostPasses.details.archivedHint', 'This offer is archived and can’t be edited.')}
        </Alert>
      ) : null}

      <Box
        sx={{
          position: 'relative',
          mb: 4,
          width: '100%',
          display: 'flex',
          flexDirection: { xs: 'column', md: 'row' },
          alignItems: 'stretch',
          borderRadius: 3,
          overflow: 'hidden',
          bgcolor: 'rgba(255,255,255,0.03)',
          border: '1px solid',
          borderColor: 'divider',
          boxShadow: '0 18px 48px rgba(0,0,0,0.35)',
          backgroundImage:
            'radial-gradient(ellipse 70% 80% at 0% 50%, rgba(78,103,235,0.16), transparent 55%), radial-gradient(ellipse 50% 70% at 100% 50%, rgba(255,78,113,0.07), transparent 50%)',
          minHeight: { md: 280 },
        }}
      >
        <Stack
          direction="row"
          spacing={0.5}
          alignItems="center"
          sx={{
            position: 'absolute',
            top: { xs: 10, sm: 14 },
            right: { xs: 10, sm: 14 },
            zIndex: 2,
          }}
        >
          <PassTargetTypeChip targetType={offer.targetType} />
          <PassOfferStatusChip status={offer.status} />
          {!isArchived ? (
            <Tooltip title={t('common.edit', 'Edit')}>
              <IconButton
                size="small"
                aria-label={t('common.edit', 'Edit')}
                onClick={() => navigate(`/host/passes/${offer.id}/edit`)}
                sx={{
                  ml: 0.25,
                  color: 'text.secondary',
                  '&:hover': { color: 'text.primary', bgcolor: 'action.hover' },
                }}
              >
                <EditOutlinedIcon fontSize="small" />
              </IconButton>
            </Tooltip>
          ) : null}
        </Stack>
        <Box
          sx={{
            position: 'absolute',
            left: 0,
            top: 0,
            bottom: 0,
            width: 5,
            bgcolor: 'primary.main',
            display: { xs: 'none', md: 'block' },
          }}
        />

        <Box
          sx={{
            flex: '1 1 auto',
            minWidth: 0,
            display: 'flex',
            flexDirection: 'column',
            pl: { xs: 2.5, sm: 3, md: 3.5 },
            pr: { xs: 2.5, sm: 3 },
            pt: { xs: 2.5, sm: 3 },
            pb: { xs: 2, md: 3 },
          }}
        >
          <Stack spacing={1} sx={{ mb: 1, minWidth: 0, pr: { xs: 16, sm: 20 } }}>
            <Stack direction="row" spacing={1} alignItems="flex-start" sx={{ minWidth: 0 }}>
              <ConfirmationNumberIcon color="primary" sx={{ fontSize: 26, flexShrink: 0, mt: 0.35 }} />
              <Typography
                variant="h4"
                component="h1"
                fontWeight={800}
                sx={{ lineHeight: 1.15, letterSpacing: '-0.02em', fontSize: { xs: '1.4rem', sm: '1.65rem' } }}
              >
                {offer.title}
              </Typography>
            </Stack>
          </Stack>

          <Box sx={{ mt: 0.5, mb: 0 }}>
            <PassTargetCollection
              targetType={offer.targetType}
              targetIds={offer.targetIds ?? []}
              targets={targets}
              audience="host"
              onRoomClick={(target) => navigate(`/host/rooms/${target.id}`)}
              onLessonClick={(target) => navigate(`/host/lesson-options/${target.id}`)}
            />
          </Box>

          <Box sx={{ flex: 1 }} />

          <Box
            sx={{
              display: 'grid',
              gridTemplateColumns: { xs: '1fr 1fr', sm: `repeat(${metaItems.length}, 1fr)` },
              gap: { xs: 1.25, sm: 1.75 },
              pt: 2,
              mt: 'auto',
            }}
          >
            {metaItems.map((item) => (
              <Box key={item.label}>
                <Typography
                  variant="caption"
                  color="text.secondary"
                  sx={{ display: 'block', letterSpacing: '0.06em', textTransform: 'uppercase', mb: 0.25 }}
                >
                  {item.label}
                </Typography>
                <Typography variant="body2" fontWeight={600}>
                  {item.value}
                </Typography>
              </Box>
            ))}
          </Box>
        </Box>

        <Box
          sx={{
            position: 'relative',
            flexShrink: 0,
            width: { xs: 'auto', md: 28 },
            height: { xs: 28, md: 'auto' },
            alignSelf: 'stretch',
            '&::before, &::after': {
              content: '""',
              position: 'absolute',
              width: 18,
              height: 18,
              borderRadius: '50%',
              bgcolor: 'background.default',
              border: '1px solid',
              borderColor: 'divider',
              zIndex: 1,
            },
            '@media (max-width: 899.95px)': {
              mx: 2,
              '&::before, &::after': {
                top: '50%',
                transform: 'translateY(-50%)',
              },
              '&::before': { left: -19 },
              '&::after': { right: -19 },
            },
            '@media (min-width: 900px)': {
              my: 0,
              '&::before, &::after': {
                left: '50%',
                transform: 'translateX(-50%)',
              },
              '&::before': { top: -10 },
              '&::after': { bottom: -10 },
            },
          }}
        >
          <Box
            sx={{
              position: 'absolute',
              borderColor: 'divider',
              top: { xs: '50%', md: 12 },
              bottom: { xs: 'auto', md: 12 },
              left: { xs: 8, md: '50%' },
              right: { xs: 8, md: 'auto' },
              height: { xs: 0, md: 'auto' },
              width: { xs: 'auto', md: 0 },
              borderTop: { xs: '2px dashed', md: 'none' },
              borderLeft: { xs: 'none', md: '2px dashed' },
              transform: { xs: 'translateY(-50%)', md: 'translateX(-50%)' },
            }}
          />
        </Box>

        <Box
          sx={{
            flex: { xs: '0 0 auto', md: `0 0 ${PASS_BOARDING_STUB_WIDTH}px` },
            width: { md: PASS_BOARDING_STUB_WIDTH },
            display: 'flex',
            flexDirection: 'column',
            justifyContent: 'center',
            px: { xs: 2.5, sm: 3 },
            pt: { xs: 2.5, md: 5 },
            pb: { xs: 2.5, md: 3 },
            bgcolor: { md: 'rgba(0,0,0,0.18)' },
          }}
        >
          <Typography
            variant="overline"
            color="text.secondary"
            sx={{ letterSpacing: '0.12em', display: 'block', mb: 0.5 }}
          >
            {t('hostPasses.price', 'Price')}
          </Typography>
          <Typography
            variant="h4"
            fontWeight={800}
            sx={{ letterSpacing: '-0.03em', fontSize: { xs: '1.5rem', md: '1.65rem' }, mb: 0.75, lineHeight: 1.2 }}
          >
            {formatPassPrice(offer, i18n.language)}
          </Typography>
          <Typography variant="body2" color="text.secondary" sx={{ mb: 1.5 }}>
            {passEntitlementLabel(offer, t)}
          </Typography>
          <Typography variant="caption" color="text.secondary">
            {t('hostPasses.soldCount', '{{count}} sold', { count: purchaseCount })}
            {' · '}
            {t('hostPasses.activeInstanceCount', '{{count}} active', { count: activeCount })}
          </Typography>
        </Box>
      </Box>

      <PassOfferInstancesPanel passId={offer.id} />

      <NotificationSnackbar
        open={snackbar.open}
        message={snackbar.message}
        severity={snackbar.severity}
        onClose={() => setSnackbar((prev) => ({ ...prev, open: false }))}
      />
    </Box>
  );
};

export default HostPassOfferDetailsPage;
